import {
  FARMS_URL_PARAM_DIAGNOSTICS_LENS,
  FARMS_URL_PARAM_LENS,
  FARMS_URL_PARAM_NODE,
  FARMS_URL_PARAM_PLANNING_LENS,
  FARMS_URL_PARAM_TAB,
  FARMS_URL_PARAM_YEAR,
} from 'constants/routeConstants';
import { INPUT_DOC_LENS_SUMMARY_2 } from 'constants/uiConstants';

export interface FarmsLinkTarget {
  /** Tree node to select, e.g. `paddock-<id>`. Omit to keep the current selection. */
  nodeKey?: string;
  /** Workspace tab. Naming a tab resets every lens param, so the tab opens on its default lens unless the target names one. */
  tab?: string;
  insightsLens?: string;
  diagnosticsLens?: string;
  /** Planning lens (OP-2549), e.g. Nutrition. */
  planningLens?: string;
  /** Section anchor to scroll to, without the leading `#`. */
  /** Calendar year to show in the tree. Omit to keep the current year filter. */
  year?: number;
  hash?: string;
}

export interface FarmsLocation {
  pathname: string;
  search: string;
  hash: string;
}

/**
 * The location a cross-view link opens. Every URL param the target doesn't
 * name (notably the tree year and group-by) is kept, so a link changes the
 * view without losing the user's place.
 */
export const buildFarmsLink = (
  pathname: string,
  search: string,
  target: FarmsLinkTarget
): FarmsLocation => {
  const next = new URLSearchParams(search);

  if (target.nodeKey) {
    next.set(FARMS_URL_PARAM_NODE, target.nodeKey);
    // A new scope opens its default Diagnostics lens (Rotation efficiency for
    // a farm or org, WUE trend for a paddock) unless the link names one.
    next.delete(FARMS_URL_PARAM_DIAGNOSTICS_LENS);
  }

  if (target.year != null) next.set(FARMS_URL_PARAM_YEAR, String(target.year));

  if (target.tab) {
    next.set(FARMS_URL_PARAM_TAB, target.tab);
    next.delete(FARMS_URL_PARAM_LENS);
    next.delete(FARMS_URL_PARAM_DIAGNOSTICS_LENS);
    next.delete(FARMS_URL_PARAM_PLANNING_LENS);
  }

  if (target.insightsLens) {
    if (target.insightsLens === INPUT_DOC_LENS_SUMMARY_2) {
      next.delete(FARMS_URL_PARAM_LENS);
    } else {
      next.set(FARMS_URL_PARAM_LENS, target.insightsLens);
    }
  }

  if (target.diagnosticsLens) {
    next.set(FARMS_URL_PARAM_DIAGNOSTICS_LENS, target.diagnosticsLens);
  }

  if (target.planningLens) {
    next.set(FARMS_URL_PARAM_PLANNING_LENS, target.planningLens);
  }

  const query = next.toString();
  return {
    pathname,
    search: query ? `?${query}` : '',
    hash: target.hash ? `#${target.hash}` : '',
  };
};
